/**
 * FanPollWidget - Displays fan poll on game pages
 * Allows users to vote and shows results
 */
import { useState, useEffect } from 'react';
import { Vote, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const FanPollWidget = ({ broadcastId }) => {
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPoll();
  }, [broadcastId]);

  const fetchPoll = async () => {
    try {
      const token = localStorage.getItem("userToken");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/fan-polls/game/${broadcastId}`, { headers });
      setPoll(res.data);
    } catch (e) {
      console.error("Error fetching poll:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (optionId) => {
    if (poll?.has_voted || poll?.is_expired) return;
    
    setVoting(true);
    setError('');
    
    try {
      const token = localStorage.getItem("userToken");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(
        `${API}/fan-polls/vote/${poll.id}`,
        { option_id: optionId },
        { headers }
      );
      setPoll(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to vote');
    } finally {
      setVoting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-900 border border-white/10 p-6">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
        </div>
      </div>
    );
  }

  if (!poll) return null;

  return (
    <div className="bg-zinc-900 border border-white/10 p-6" data-testid="fan-poll-widget">
      <h3 className="font-secondary text-sm font-bold uppercase text-zinc-400 mb-4 flex items-center gap-2">
        <Vote className="w-4 h-4 text-orange-500" />
        Fan Poll
      </h3>
      
      <p className="text-white font-bold mb-4">{poll.question}</p>
      
      {error && (
        <p className="text-red-500 text-sm mb-3">{error}</p>
      )}
      
      <div className="space-y-3">
        {poll.options?.map(option => (
          <div key={option.id}>
            {poll.show_results ? (
              // Show results after voting
              <div className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-300">{option.text}</span>
                  <span className="text-zinc-500">{option.percentage || 0}%</span>
                </div>
                <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-orange-500 rounded-full transition-all duration-500"
                    style={{ width: `${option.percentage || 0}%` }}
                  />
                </div>
              </div>
            ) : (
              // Show vote button
              <button
                onClick={() => handleVote(option.id)}
                disabled={voting || poll.is_expired}
                className={`w-full py-3 px-4 text-left font-medium transition-all border ${
                  voting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-orange-500/20 hover:border-orange-500/50'
                } bg-zinc-800 border-white/10 text-white rounded`}
              >
                {option.text}
              </button>
            )}
          </div>
        ))}
      </div>
      
      {/* Total votes */}
      <p className="text-zinc-500 text-xs mt-4 text-center">
        {poll.total_votes} {poll.total_votes === 1 ? 'vote' : 'votes'}
        {poll.has_voted && ' • You voted'}
        {poll.is_expired && ' • Poll ended'}
      </p>
      
      {/* Expiration warning */}
      {poll.expires_at && !poll.is_expired && (
        <p className="text-orange-500/70 text-xs mt-1 text-center">
          Ends {new Date(poll.expires_at).toLocaleDateString()}
        </p>
      )}
    </div>
  );
};

export default FanPollWidget;
